import { useCallback, useEffect, useState } from 'react';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { DrawerItem } from 'helpers/drawer-config';
import { useGlobalContext } from 'context/GlobalContext';

import DrawerMenuItem from '../DrawerMenuItem';

type DrawerMenuItemWithSubMenuProps = {
  addMenuIndex: (index: number) => void;
  index: number;
  item: DrawerItem;
  open?: boolean;
  isMenuOpen: boolean;
  handleExpandDrawer?: () => void;
};

/**
 * DrawerMenuItemWithSubMenu Component
 *
 * Represents a drawer menu item with sub-menu in the main layout.
 *
 * @component
 * @param {Object} DrawerMenuItemWithSubMenuProps - The properties for the component.
 * @param {Function} DrawerMenuItemWithSubMenuProps.addMenuIndex - Function to add menu index.
 * @param {number} DrawerMenuItemWithSubMenuProps.index - Index of the menu item.
 * @param {Object} DrawerMenuItemWithSubMenuProps.item - Menu item data.
 * @param {boolean} [DrawerMenuItemWithSubMenuProps.open=true] - Flag indicating if the menu is open.
 * @param {boolean} [DrawerMenuItemWithSubMenuProps.isMenuOpen=true]- Flag indicating if the sub-menu is open.
 * @param {Function} DrawerMenuItemWithSubMenuProps.handleExpandDrawer - Function to handle expanding the drawer.
 * @returns {JSX.Element|null} The rendered component or null if the user does not have permission.
 */
function DrawerMenuItemWithSubMenu({
  addMenuIndex,
  index,
  item,
  open,
  isMenuOpen,
  handleExpandDrawer
}: DrawerMenuItemWithSubMenuProps): JSX.Element | null {
  const { hasPerm } = useGlobalContext();
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [openMenuIndex, setOpenMenuIndex] = useState<number>();

  useEffect(() => {
    if (isMenuOpen === false) {
      setOpenMenu(false);
      setOpenMenuIndex(null);
    }
  }, [isMenuOpen]);

  const handleOpenMenu = () => {
    addMenuIndex(index);
    setOpenMenu((prevState) => !prevState);
    if (handleExpandDrawer) handleExpandDrawer();
  };

  const handleAddMenuIndex = useCallback((subIndex: number) => {
    setOpenMenuIndex(subIndex);
  }, []);

  if (!hasPerm(item.permissions)) {
    return null;
  }

  let icon = null;

  if (item.submenu.length > 0) {
    icon = openMenu ? <ExpandMore /> : <ExpandLess />;
  }

  return (
    <>
      <ListItemButton
        disabled={item.disabled}
        onClick={
          item.url
            ? () => window.open(item.url, '_blank', 'noopener,noreferrer')
            : handleOpenMenu
        }
        style={open ? { margin: '0px 10px' } : {}}
      >
        <ListItemIcon style={{ color: '#FFFFFF' }}>
          <Tooltip title={item.title}>
            <span>{item.icon}</span>
          </Tooltip>
        </ListItemIcon>
        <ListItemText
          primary={item.title}
          sx={{
            whiteSpace: 'normal'
          }}
        />
        {icon}
      </ListItemButton>
      <Collapse in={!open ? false : openMenu} timeout="auto">
        <List
          component="div"
          style={{ background: 'rgb(9, 23, 46)' }}
          disablePadding
        >
          {item.submenu.map((subItem, idx) =>
            subItem.submenu ? (
              <DrawerMenuItemWithSubMenu
                addMenuIndex={handleAddMenuIndex}
                index={idx}
                isMenuOpen={openMenuIndex === idx}
                item={subItem}
                key={subItem.key}
                open={open}
              />
            ) : (
              <DrawerMenuItem
                activeSubMenu={subItem.activeSubMenu}
                item={subItem}
                key={subItem.key}
                open={open}
              />
            )
          )}
        </List>
      </Collapse>
    </>
  );
}

export default DrawerMenuItemWithSubMenu;
